import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
//primereact
import { Button } from "primereact/button";
//components
import Modal from "../../../../components/modal/Modal";
import Input from "../../../../components/input/Input";
import Select from "../../../../components/input/Select";
import MaskInput from "../../../../components/input/MaskInput";
import TextareaInput from "../../../../components/input/TextareaInput";
//utils
import { states } from "../../../../utils/states";
import { handleCep } from "../../../../utils/handleCep";
import { queryClient } from "../../../../utils/queryClient";
//hooks
import { useOptionsSelect } from "../../../../hooks/useOptionsSelect";
//services
import { postLead } from "../../../../services/Crm/Funnel/postLead";
import { getOrigins } from "../../../../services/Crm/Settings/Origins/getOrigins";
import { getFormLeadConfig } from "../../../../services/Crm/Settings/FormLead/getFormLeadConfig";
import { get } from "js-cookie";
import { getTeamList } from "../../../../services/GlobalRoles/getTeamList";

const ModalAddLead = ({
  visible,
  setVisible,
  funnelId,
  setEditedId,
  baseData,
  setBaseData,
}) => {
  const form = useForm();
  const [address, setAddress] = useState(null);
  const [teamsOptions, setTeamsOptions] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [inputRequireds, setInputRequireds] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [originsSelect, setOriginsSelect] = useState([]);
  const {
    enterprise,
    isLoadingEnterprises,
    selectTeam,
    brokers,
    isLoadingBroker,
  } = useOptionsSelect();

  useQuery({
    queryKey: [`formLead`],
    queryFn: () => getFormLeadConfig(),
    onSuccess: (data) => {
      const requiredFields = data?.required.map((field) => field.name);
      setInputRequireds(requiredFields);
    },
  });

  const { data: _, isLoading: isLoadingOrigins } = useQuery({
    queryKey: [`origins`],
    queryFn: () => getOrigins(),
    onSuccess: (data) => {
      const options = data.map((o) => {
        return {
          value: o.id,
          label: o.name,
        };
      });
      setOriginsSelect(options);
    },
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: postLeadFn } = useMutation({
    mutationFn: postLead,
    onSuccess(res, variables) {
      queryClient.setQueryData(["kanban", funnelId], (data) => {
        if (!data || !Array.isArray(data.status)) return data;
        const updatedStatus = [...data.status];
        const firstColumn = updatedStatus[0];
        if (!firstColumn) return data;
        const newLead = {
          ...res.success,
          id: res.success.id,
          address: variables.address,
          pivot: {
            status_id: variables.status_id,
            lead_id: res.success.id,
            funnel_id: variables.funnel_id,
            created_at: res.success.created_at,
            updated_at: res.success.updated_at, 
          },
        };
        firstColumn.leads = [newLead, ...(firstColumn.leads || [])];
        updatedStatus[0] = firstColumn;
        setEditedId(res.success.id);
        return {
          ...data,
          status: updatedStatus,
        };
      });
    },
  });

  const addNewLead = async (data) => {
    const notification = toast.loading("Aguarde...");
    setLoadingButton(true);
    
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== "")
    );

    const {
      cep,
      num,
      complement,
      city,
      state,
      address,
      neighborhood,
      ...rest
    } = filteredData;

    const sendData = {
      ...rest,
      funnel_id: funnelId,
      address: {
        cep,
        num,
        complement,
        city,
        state,
        address,
        neighborhood,
      },
    };

    try {
      await postLeadFn(sendData);

      toast.update(notification, {
        render: "Lead cadastrado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      form.reset();
      setLoadingButton(false);
      setVisible(false);
    } catch (error) {
      if (error.response.data.errors.email) {
        toast.update(notification, {
          render: "Email já utilizado no sistema!",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        form.setError("email", {
          type: "manual",
          message: "Email já utilizado no sistema!",
        });
      } else if (error.response.data.errors.phone) {
        toast.update(notification, {
          render: "Telefone já utilizado no sistema!",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        form.setError("phone", {
          type: "manual",
          message: "Telefone já utilizado no sistema!",
        });
      } else {
        toast.update(notification, {
          render: "Erro ao adicionar lead!",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
      setLoadingButton(false);
    }
  };

  const selectEnterprise = async (enterprise_id) => {
    setIsLoadingTeams(true);
    try {
      const response = await getTeamList(enterprise_id);
      // console.log("Response", response);
      const options = response.map((o) => {
        return {
          value: o.id,
          label: o.name,
        };
      });
      setTeamsOptions(options);
      setIsLoadingTeams(false);
    } catch (error) {
      console.error("Error fetching team list:", error);
      setIsLoadingTeams(false);
    }
  };

  useEffect(() => {
    if (address && Object.keys(address).length !== 0) {
      form.setValue("address", address.address);
      form.setValue("city", address.city);
      form.setValue("neighborhood", address.neighborhood);
      form.setValue("state", address.state);
      form.clearErrors(["address", "city", "neighborhood", "state"]);
    }
  }, [address]);

  return (
    <Modal
      dynamicTitle="Adicionar Lead"
      visible={visible}
      onHide={() => {
        setVisible(false);
        setBaseData(null);
      }}
    >
      <form onSubmit={form.handleSubmit(addNewLead)}>
        <div className="row">
          <div className="col-md-12">
            <div className="inline-flex align-items-center">
              <i className="pi pi-user me-2"></i>
              <b>Dados principais</b>
            </div>
          </div>
          <div className="col-md-4 my-2">
            <Input
              name="name"
              label={`Nome Completo ${
                inputRequireds.includes("name") ? "*" : ""
              }`}
              isRequired={inputRequireds.includes("name")}
              className="w-100"
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-4 my-2">
            <Input
              name="email"
              label={`E-mail ${inputRequireds.includes("email") ? "*" : ""}`}
              isRequired={inputRequireds.includes("email")}
              errors={form.formState.errors}
              control={form.control}
              type="email"
              validationRules={{
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "E-mail inválido.",
                },
              }}
            />
          </div>
          <div className="col-md-4 my-2">
            <MaskInput
              name="phone"
              label={`Celular ${inputRequireds.includes("phone") ? "*" : ""}`}
              isRequired={inputRequireds.includes("phone")}
              mask="(99) 99999-9999"
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-12 mt-3">
            <div className="inline-flex align-items-center">
              <i className="pi pi-map me-2"></i>
              <b>Endereço</b>
            </div>
          </div>
          <div className="col-md-3">
            <MaskInput
              name="cep"
              label={`CEP ${inputRequireds.includes("cep") ? "*" : ""}`}
              isRequired={inputRequireds.includes("cep")}
              mask="99999-999"
              onComplete={() => handleCep(form.getValues("cep"), setAddress)}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-3">
            <Input
              name="num"
              label={`Número ${inputRequireds.includes("numero") ? "*" : ""}`}
              isRequired={inputRequireds.includes("numero")}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-6">
            <Input
              name="complement"
              label={`Complemento ${
                inputRequireds.includes("complement") ? "*" : ""
              }`}
              isRequired={inputRequireds.includes("complement")}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-3 mt-2">
            <Input
              name="city"
              label={`Cidade ${inputRequireds.includes("city") ? "*" : ""}`}
              isRequired={inputRequireds.includes("city")}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-3 mt-2">
            <Select
              name="state"
              label={`Estado ${inputRequireds.includes("state") ? "*" : ""}`}
              isRequired={inputRequireds.includes("state")}
              errors={form.formState.errors}
              control={form.control}
              options={states}
            />
          </div>
          <div className="col-md-3 mt-2">
            <Input
              name="address"
              label={`Rua ${inputRequireds.includes("address") ? "*" : ""}`}
              isRequired={inputRequireds.includes("address")}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-3 mt-2">
            <Input
              name="neighborhood"
              label={`Bairro ${
                inputRequireds.includes("neighborhood") ? "*" : ""
              }`}
              isRequired={inputRequireds.includes("neighborhood")}
              control={form.control}
              errors={form.formState.errors}
            />
          </div>
          <div className="col-md-12 mt-3">
            <div className="inline-flex align-items-center">
              <i className="pi pi-folder-open me-2"></i>
              <b>Tratativa do cliente</b>
            </div>
          </div>
          <div className="col-md-3">
            <Select
              name="origin_id"
              label={`Origem ${
                inputRequireds.includes("origin_id") ? "*" : ""
              }`}
              isRequired={inputRequireds.includes("origin_id")}
              disabled={isLoadingOrigins}
              errors={form.formState.errors}
              control={form.control}
              options={originsSelect}
            />
          </div>
          <div className="col-md-3">
            <Select
              isRequired
              name="enterprise_id"
              label={`Interesse *`}
              errors={form.formState.errors}
              disabled={isLoadingEnterprises}
              onChange={(e) => selectEnterprise(e)}
              control={form.control}
              options={enterprise}
            />
          </div>
          <div className="col-md-3">
            <Select
              name="team_id"
              label={
                isLoadingTeams
                  ? "Carregando..."
                  : `Time ${inputRequireds.includes("team_id") ? "*" : ""}`
              }
              isRequired={inputRequireds.includes("team_id")}
              control={form.control}
              errors={form.formState.errors}
              onChange={(e) => selectTeam(e)}
              disabled={isLoadingTeams}
              options={teamsOptions}
            />
          </div>
          <div className="col-md-3">
            <Select
              name="broker_id"
              label={
                isLoadingBroker
                  ? "Carregando..."
                  : `Corretor ${
                      inputRequireds.includes("broker_id") ? "*" : ""
                    }`
              }
              isRequired={inputRequireds.includes("broker_id")}
              errors={form.formState.errors}
              disabled={isLoadingBroker}
              control={form.control}
              options={brokers}
            />
          </div>
          <div className="col-md-12">
            <TextareaInput
              name="obs"
              label={`Observações ${inputRequireds.includes("obs") ? "*" : ""}`}
              isRequired={inputRequireds.includes("obs")}
              className="mt-4"
              errors={form.formState.errors}
              control={form.control}
            />
          </div>
          <div className="row justify-content-end">
            <div className="col-md-3 pt-3 justify-content-end d-flex">
              <Button
                rounded
                type="submit"
                label="Criar"
                iconPos="right"
                loading={loadingButton}
                className="p-inputtext-sm w-100"
                icon="fa-solid fa-circle-chevron-right"
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ModalAddLead;
